import * as React from 'react';

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@cotera/client/v0/components/ui/resizable';
import { useIsBelowMd } from '@cotera/client/app/hooks';
import { cn } from '@cotera/client/v0/lib/utils';

/** Percentage of the split the rail takes when it opens, and the floor a drag can reach. */
const RAIL_DEFAULT_SIZE = 24;
const RAIL_MIN_SIZE = 16;
const RAIL_MAX_SIZE = 40;
/** Leaves the content pane usable no matter how far the seam is dragged right. */
const CONTENT_MIN_SIZE = 40;

export interface CommentsRailLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The rail's contents, normally a `CommentsRail`. Null when nothing is commentable. */
  rail: React.ReactNode;
  /**
   * Extra insets for the rail's column, for a host whose content does not start at
   * the top of this box — the focus view, where the bar belongs to the artifact
   * rather than to the page around it.
   */
  railClassName?: string;
  children: React.ReactNode;
}

/**
 * Docks the comments rail as the last column of the page.
 *
 * A column rather than an overlay sheet, for the reason a comments list exists at
 * all: it is read against the text it annotates, and a scrim over the document hides
 * the one thing the reader needs. It is also the shape the rest of the app already
 * uses for a surface beside a primary editor — the artifact side panel, the workflow
 * runs panel — so the rail resizes, collapses and remembers its width by the same
 * gestures.
 *
 * Wrapping the artifact panel rather than joining its group keeps that panel's own
 * layout arithmetic (its animated open/close percentages) untouched, and puts
 * comments outside it: chat or editor, artifact, then comments, all three at once.
 *
 * The content pane is rendered in exactly one place, always inside the same panel
 * group, however the rail is being presented. Everything that varies — the seam, the
 * rail column, the small-screen cover — is a sibling. Giving the content a different
 * parent in some other state would unmount the editor inside it, and the editor is
 * both what the rail reads and what tells the rail it exists, so that teardown
 * feeds back into the condition that caused it.
 */
export const CommentsRailLayout: React.FC<CommentsRailLayoutProps> = ({
  open,
  onOpenChange,
  rail,
  railClassName,
  children,
}) => {
  // No room to split a phone in two, so the rail covers the content there instead.
  // The content stays mounted underneath either way.
  const isBelowMd = useIsBelowMd();
  const showing = open && rail !== null;

  return (
    <div className="relative flex h-full min-h-0 w-full flex-1 flex-col">
      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId="comments-rail-split"
        className="h-full w-full"
      >
        <ResizablePanel
          id="comments-rail-content"
          order={1}
          minSize={CONTENT_MIN_SIZE}
          className="flex min-h-0 min-w-0 flex-col"
        >
          {children}
        </ResizablePanel>
        {showing && !isBelowMd ? (
          <>
            {/* The same unmarked seam the artifact card sits against, not the
                  bordered `default` split: a card's own edge is the line here, and
                  a grip down the middle would be a second one. */}
            <ResizableHandle variant="artifact-card" />
            <ResizablePanel
              id="comments-rail"
              order={2}
              defaultSize={RAIL_DEFAULT_SIZE}
              minSize={RAIL_MIN_SIZE}
              maxSize={RAIL_MAX_SIZE}
              collapsible
              collapsedSize={0}
              // Dragging the seam shut is the same intent as the header toggle, so
              // it resolves to closed rather than leaving a sliver of a column.
              onCollapse={() => onOpenChange(false)}
              // `overflow-visible` so the card's border and shadow have somewhere
              // to land, and `pointer-events-none` so the gutter beside it belongs
              // to the resize handle — both copied from the artifact panel, whose
              // `md:pl-2` this gutter matches.
              className="min-h-0 min-w-0 overflow-visible pointer-events-none"
            >
              {/* The artifact card's own insets, copied so the two cards are
                    the same height and start on the same line: `md:py-3` off the
                    bar above and the content panel's margin below, `md:pl-2` for
                    the seam, and nothing on the right, where this edge now meets
                    `.v1-content-panel` and its 0.5rem margin — the position the
                    artifact card used to hold. See `renderArtifactPanelContent`. */}
              <div
                className={cn(
                  'pointer-events-none flex h-full min-h-0 flex-col overflow-visible md:py-3 md:pl-2',
                  railClassName
                )}
              >
                {rail}
              </div>
            </ResizablePanel>
          </>
        ) : null}
      </ResizablePanelGroup>
      {showing && isBelowMd ? (
        // Edge to edge on a phone, like the artifact: there is no canvas left for
        // a card to float on, so the card rule in `v1.css` drops its chrome below
        // `md` and keeps only the white.
        <div className="absolute inset-0 z-20">{rail}</div>
      ) : null}
    </div>
  );
};
